import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Building2, Aperture } from 'lucide-react';

const RoleSelect = () => {
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);
  const { setRole, user } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!selectedRole) {
      toast.error('Please select a role');
      return;
    }

    setLoading(true);
    try {
      await setRole(selectedRole);
      toast.success('Welcome to FrameFinder!');
      if (selectedRole === 'creative') {
        navigate('/edit-profile');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to set role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#f7f5f2' }}>
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative bg-black">
        <img
          src="https://images.pexels.com/photos/29675497/pexels-photo-29675497.jpeg"
          alt="Photography"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-16 left-16 right-16 text-white">
          <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#d4a843' }}>Almost there</p>
          <h2 className="mb-4" style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 'normal', fontSize: '3.5rem', lineHeight: 1.1 }}>FrameFinder</h2>
          <p className="text-gray-300 text-lg font-light">Tell us how you'll be using the platform</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fadeInUp">
          <h1 className="mb-2" style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 'normal', fontSize: '2.5rem' }}>
            Welcome, {user?.name?.split(' ')[0]}.
          </h1>
          <p className="mb-10" style={{ color: '#6b6b6b' }}>How would you like to use FrameFinder?</p>

          <div className="flex flex-col gap-4 mb-8">
            <button
              type="button"
              onClick={() => setSelectedRole('business')}
              className="p-6 rounded-[14px] border-2 transition-all text-left flex items-center gap-4"
              style={{
                borderColor: selectedRole === 'business' ? '#d4a843' : '#e4e1db',
                background: selectedRole === 'business' ? '#fdf8ef' : 'white',
              }}
              data-testid="select-business-btn"
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                background: selectedRole === 'business' ? '#d4a843' : '#f0ede8',
              }}>
                <Building2 className="w-6 h-6" strokeWidth={1.5} style={{ color: selectedRole === 'business' ? 'white' : '#5a5a5a' }} />
              </div>
              <div>
                <h3 className="font-semibold mb-0.5" style={{ color: '#0e0e0e' }}>I'm a Business</h3>
                <p className="text-sm" style={{ color: '#6b6b6b' }}>I want to hire photographers and videographers</p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setSelectedRole('creative')}
              className="p-6 rounded-[14px] border-2 transition-all text-left flex items-center gap-4"
              style={{
                borderColor: selectedRole === 'creative' ? '#d4a843' : '#e4e1db',
                background: selectedRole === 'creative' ? '#fdf8ef' : 'white',
              }}
              data-testid="select-creative-btn"
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                background: selectedRole === 'creative' ? '#d4a843' : '#f0ede8',
              }}>
                <Aperture className="w-6 h-6" strokeWidth={1.5} style={{ color: selectedRole === 'creative' ? 'white' : '#5a5a5a' }} />
              </div>
              <div>
                <h3 className="font-semibold mb-0.5" style={{ color: '#0e0e0e' }}>I'm a Photographer</h3>
                <p className="text-sm" style={{ color: '#6b6b6b' }}>I'm a photographer or videographer looking for work</p>
              </div>
            </button>
          </div>

          <Button
            onClick={handleSubmit}
            className="w-full h-12 rounded-full font-medium text-white"
            style={{ background: '#0e0e0e' }}
            disabled={!selectedRole || loading}
            data-testid="confirm-role-btn"
          >
            {loading ? 'Setting up...' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RoleSelect;
